import type { FilterField } from '@/shared/components/filter/types'
import type { FieldSchemaModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { TFunction } from 'i18next'

import { useMemo } from 'react'
import {
	any,
	filter,
	map,
	merge,
	orderBy,
	paths,
	pipe,
	prop,
	uniqBy,
} from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBiFieldsQuery, useBiFieldValuesQuery } from '@/entities/bi'
import { toAdaptedSchema } from '@/entities/column'
import {
	toCategoriesTree,
	useBrandsQuery,
	useCategoriesQuery,
	useCompetitorsOptionsQuery,
} from '@/entities/core'
import {
	getSalesEntitiesTree,
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import {
	getPricingCampaignEngines,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import {
	Operators,
	ValueEditorType,
	ValueType,
} from '@/shared/components/query-builder'
import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import { toOption } from '@/shared/tools/utils'

const formatOptionsById = pipe([
	map(paths(['id', 'name'])),
	toOption,
	orderBy(['label'], ['asc']),
	uniqBy('value'),
])
const formatOptionsByName = pipe([
	map(paths(['name', 'name'])),
	toOption,
	orderBy(['label'], ['asc']),
	uniqBy('value'),
])

export function useFilterFieldsConfig({
	sanitizeFields,
}: {
	sanitizeFields?: (fieldsConfig: Record<string, any>) => Record<string, any>
}) {
	const { t } = useTranslation()

	const biFieldsQuery = useBiFieldsQuery({
		select: filter(prop('params.filterable')),
	})
	const brandsQuery = useBrandsQuery(
		{},
		{
			select: pipe([
				map(paths(['id', 'name'])),
				toOption,
				orderBy(['label'], ['asc']),
				uniqBy('value'),
			]),
		}
	)
	const competitorsQuery = useCompetitorsOptionsQuery(
		{
			limit: 0,
		},
		{
			select: (data) =>
				data.objects?.map((competitor) => ({
					value: competitor.name!,
					label: competitor.name!,
				})),
		}
	)
	const salesEntitiesQuery = useSalesEntitiesQuery()
	const pricingCampaignsQuery = usePricingCampaignsQuery({})
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const categoriesQuery = useCategoriesQuery<TreeNodeType[]>(
		{},
		{
			select: toCategoriesTree,
		}
	)
	const dataChoices = useMemo(
		() => ({
			competitor_name: competitorsQuery.data || [],
			brand: brandsQuery.data || [],
			category_id: categoriesQuery.data || [],
			category_ids: categoriesQuery.data || [],
			optimization_group_id:
				formatOptionsById(optimizationGroupsQuery.data) || [],
			optimization_group:
				formatOptionsByName(optimizationGroupsQuery.data) || [],
			pos_id: getSalesEntitiesTree(salesEntitiesQuery.data) || [],
			pos_ids: getSalesEntitiesTree(salesEntitiesQuery.data) || [],
			pricing_campaign_name:
				formatOptionsByName(pricingCampaignsQuery.data) || [],
			pricing_campaign_engine: getPricingCampaignEngines(t),
		}),
		[
			competitorsQuery.data,
			optimizationGroupsQuery.data,
			brandsQuery.data,
			pricingCampaignsQuery.data,
			categoriesQuery.data,
			salesEntitiesQuery.data,
		]
	)
	const fields = useMemo(
		() =>
			uniqBy('value', [
				...getDefautFields(t),
				...map(
					pipe([
						(f: FieldSchemaModel) => ({
							...toAdaptedSchema(f, t),
							dynamic: (
								f.options as {
									is_dynamic_values?: boolean
								}
							)?.is_dynamic_values,
						}),
						extendEditorTypes,
					]),
					[...commonSchema, ...(biFieldsQuery.data || [])]
				),
			]) as unknown as FilterField[],
		[biFieldsQuery.data]
	)
	const fieldsConfig = useMemo(() => {
		const config = getFiledConfig(fields, dataChoices)
		const extendedConfig = biFieldsQuery.data
			?.filter((schema) => schema.options.is_dynamic_values)
			.reduce((acc, schema) => {
				acc[schema.id] = {
					useSelectOptions() {
						return useBiFieldValuesQuery(schema.id, {
							select: (list) => list?.map((value) => ({ value, label: value })),
						})
					},
				}

				return acc
			}, {})

		return merge(config, extendedConfig)
	}, [fields, dataChoices])
	const isLoading = any(prop('isLoading'), [
		optimizationGroupsQuery,
		brandsQuery,
		pricingCampaignsQuery,
		categoriesQuery,
		biFieldsQuery,
		salesEntitiesQuery,
	])

	return useMemo(
		() => ({
			fields,
			dataChoices,
			fieldsConfig: sanitizeFields
				? sanitizeFields(fieldsConfig)
				: fieldsConfig,
			isLoading,
		}),
		[fields, dataChoices, fieldsConfig, isLoading]
	)
}
/**
 * Helping function to extend editor types base on field options
 * @param field field to extend
 * @returns extended field
 */
const extendEditorTypes = (field: FilterField & { dynamic: boolean }) => {
	if (
		(field.enum?.length && field.valueType === ValueType.string) ||
		(field.dynamic && field.valueType === ValueType.string) ||
		(field.dynamic && field.valueType === ValueType.array)
	) {
		return {
			...field,
			valueEditorType: ValueEditorType.multiselect,
		}
	}

	return field
}

const commonSchema = [
	{
		id: 'optimization_group_id',
		value_type: ValueType.string,
		value_editor_type: ValueEditorType.multiselect,
		name: 'field_schema_optimization_group',
	},
	{
		id: 'pos_id',
		value_type: ValueType.string,
		value_editor_type: ValueEditorType.checkboxTree,
		name: 'field_schema_pos_id',
	},
	{
		id: 'pos_ids',
		value_type: ValueType.array,
		value_editor_type: ValueEditorType.checkboxTree,
		name: 'field_schema_pos_id',
	},
]

export const getDefautFields = (t: TFunction) => [
	{
		value: 'pricing_campaign_engine',
		label: t('app.pricing_type', { defaultValue: 'Pricing type' }),
		enum: [],
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
	},

	{
		value: 'optimization_group',
		label: t('optimization_group', { defaultValue: 'Optimization group' }),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
		enum: [],
	},
	{
		value: 'pricing_campaign_name',
		label: t('pricing_campaign', { defaultValue: 'Pricing Campaign' }),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
		enum: [],
	},
	{
		value: 'brand',
		label: t('app.brand', { defaultValue: 'Brand' }),
		valueType: ValueType.string,
		operations: [Operators.IN],
		valueEditorType: ValueEditorType.multiselect,
		enum: [],
	},
	{
		value: 'category_id',
		label: t('app.category', { defaultValue: 'Category' }),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.checkboxTree,
		enum: [],
	},
	{
		value: 'sku',
		label: 'SKU',
		valueType: ValueType.string,
		enum: [],
	},
	{
		value: 'pricing_line_id',
		label: t('pricing_line_id'),
		valueType: ValueType.string,
		enum: [],
	},
	{
		value: 'category_ids',
		label: t('field_schema_categories', { ns: 'fields' }),
		valueType: ValueType.array,
		operations: [Operators.ANY_OF],
		valueEditorType: ValueEditorType.checkboxTree,
		enum: [],
	},
	{
		value: 'competitor_name',
		label: t('app.competitor', { defaultValue: 'Competitor' }),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.multiselect,
		operations: [Operators.IN],
		enum: [],
	},
]
